import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, X, Copy, Mail, MessageCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { I18nBadge, LanguageSelect, useActiveLanguages } from "@/components/email/EmailI18n";

/**
 * Iter 135 — Modal de Reserva PayPal para una reparación.
 *
 * Flujo: introducir importe → Generar enlace → mostrar link + enviar por
 * Email/WhatsApp. Anti-doble-clic: botón se deshabilita durante `busy`
 * (backend además dedupea reservas del mismo user creadas <10s antes).
 */
export default function ReservationModal({ repair, customer, open, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [sending, setSending] = useState(false);
  const [waPreview, setWaPreview] = useState(null);
  const [emailLang, setEmailLang] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const languages = useActiveLanguages();

  useEffect(() => {
    if (!reservation || !customer?.email) return;
    api.post(`/repairs/${repair.id}/reservations/${reservation.id}/notify/preview`, { channel: "email", language_code: emailLang || null })
      .then((r) => setEmailPreview(r.data))
      .catch(() => setEmailPreview(null));
  }, [reservation, emailLang]); // eslint-disable-line

  if (!open) return null;

  const reset = () => {
    setAmount("");
    setReservation(null);
    setWaPreview(null);
    setEmailLang("");
    setEmailPreview(null);
  };

  const closeAll = () => {
    reset();
    onClose && onClose();
  };

  const generate = async () => {
    const val = Number(String(amount).replace(",", "."));
    if (!val || val <= 0) {
      toast.error("Introduce un importe válido en euros.");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repair.id}/reservations`, { amount: val });
      setReservation(r.data);
      toast.success("Enlace PayPal generado");
      onCreated && onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!reservation || sending) return;
    setSending(true);
    try {
      const r = await api.post(
        `/repairs/${repair.id}/reservations/${reservation.id}/notify`,
        { channel: "email", language_code: emailLang || null },
      );
      toast.success(`Email enviado a ${r.data.to}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = async () => {
    if (!reservation || sending) return;
    setSending(true);
    try {
      const r = await api.post(
        `/repairs/${repair.id}/reservations/${reservation.id}/notify/preview`,
        { channel: "whatsapp" },
      );
      setWaPreview(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = async () => {
    if (!reservation || !waPreview) return;
    try {
      const r = await api.post(
        `/repairs/${repair.id}/reservations/${reservation.id}/notify`,
        { channel: "whatsapp", override_body: waPreview.body },
      );
      window.open(r.data.wa_link, "_blank", "noopener,noreferrer");
      toast.success("WhatsApp abierto en pestaña nueva");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="reservation-modal"
      onClick={(e) => e.target === e.currentTarget && closeAll()}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header verde CRM */}
        <div className="px-5 py-3 bg-[#6E8B3D] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard size={18} />
            <h2 className="font-display font-black text-lg">RESERVA — {repair.code}</h2>
          </div>
          <button onClick={closeAll} className="hover:bg-white/20 rounded p-1" data-testid="reservation-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Datos automáticos */}
          <div className="text-sm space-y-1 bg-gray-50 border rounded p-3">
            <div><strong>Cliente:</strong> {customer?.name || repair.customer_name || "—"}</div>
            <div><strong>Email:</strong> {customer?.email || "—"}</div>
            <div><strong>Teléfono:</strong> {customer?.phone || "—"}</div>
            <div><strong>Equipo:</strong> {repair.device_type || "—"} — {repair.model || "—"}</div>
            <div><strong>Nº serie:</strong> {repair.serial || "—"}</div>
          </div>

          {!reservation && (
            <>
              <label className="text-sm block">
                <div className="mb-1 font-semibold">Importe de la reserva (€, IVA incluido)</div>
                <input
                  type="text"
                  inputMode="decimal"
                  className="w-full border px-3 py-2 text-lg"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50,00"
                  disabled={busy}
                  data-testid="reservation-amount-input"
                  autoFocus
                />
              </label>
              <button
                onClick={generate}
                disabled={busy || !amount}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#6E8B3D] text-white px-3 py-2 font-semibold hover:bg-[#5c7534] disabled:opacity-50"
                data-testid="reservation-generate-btn"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                Generar reserva
              </button>
            </>
          )}

          {reservation && (
            <div className="space-y-3" data-testid="reservation-created-box">
              <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm">
                <div className="font-semibold text-green-900 mb-1">
                  Reserva creada por {reservation.amount.toFixed(2)} €
                </div>
                <div className="text-xs text-gray-600">Order ID: <code>{reservation.paypal_order_id}</code></div>
              </div>

              <label className="text-sm block">
                <div className="mb-1 font-semibold">Enlace PayPal</div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={reservation.paypal_approval_url}
                    className="flex-1 border px-2 py-1 text-xs font-mono"
                    data-testid="reservation-link-input"
                  />
                  <button
                    className="border px-2 py-1 text-xs hover:bg-gray-50"
                    onClick={() => { navigator.clipboard.writeText(reservation.paypal_approval_url); toast.success("Copiado"); }}
                    data-testid="reservation-link-copy"
                  >
                    <Copy size={12} className="inline" />
                  </button>
                </div>
              </label>

              {emailPreview?.i18n && (
                <div className="border rounded-md bg-gray-50 p-3 space-y-2" data-testid="reservation-email-i18n">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <I18nBadge i18n={emailPreview.i18n} testid="reservation-i18n-badge" />
                    <LanguageSelect value={emailLang || emailPreview.customer_language} onChange={setEmailLang} languages={languages} customerLanguage={emailPreview.customer_language} testid="reservation-language-select" />
                  </div>
                  <div className="text-xs"><span className="text-gray-500">Asunto:</span> <span data-testid="reservation-email-subject">{emailPreview.subject}</span></div>
                  <button type="button" onClick={() => setShowEmailPreview(!showEmailPreview)} className="text-xs underline text-gray-600" data-testid="reservation-email-preview-toggle">
                    {showEmailPreview ? "Ocultar vista previa" : "Ver vista previa del email"}
                  </button>
                  {showEmailPreview && (
                    <iframe title="Vista previa email reserva" srcDoc={emailPreview.body_html || emailPreview.body} className="w-full h-64 bg-white border" data-testid="reservation-email-iframe" />
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={sendEmail}
                  disabled={sending || !customer?.email}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
                  data-testid="reservation-send-email"
                >
                  <Mail size={14} /> Enviar por Email
                </button>
                <button
                  onClick={openWhatsApp}
                  disabled={sending || !customer?.phone}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
                  data-testid="reservation-send-whatsapp"
                >
                  <MessageCircle size={14} /> Enviar por WhatsApp
                </button>
              </div>

              {waPreview && (
                <div className="rounded-md border p-3 space-y-2 bg-gray-50" data-testid="reservation-wa-preview">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Vista previa WhatsApp (editable)</div>
                  <textarea
                    value={waPreview.body}
                    onChange={(e) => setWaPreview({ ...waPreview, body: e.target.value })}
                    rows={6}
                    className="w-full border px-2 py-1 text-sm"
                    data-testid="reservation-wa-body"
                  />
                  <button
                    onClick={sendWhatsApp}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] text-white px-3 py-2 font-semibold hover:brightness-90"
                    data-testid="reservation-wa-confirm"
                  >
                    <MessageCircle size={14} /> Abrir en WhatsApp
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end bg-gray-50">
          <button onClick={closeAll} className="px-3 py-1.5 text-sm border rounded-md hover:bg-white">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
